'use client';

import { useMemo, useState } from 'react';
import {
  BADGE_COLOR_PRESETS,
  DEFAULT_BADGE,
  buildBadgeHtml,
  buildBadgeMarkdown,
  buildBadgeSvg,
  sanitizeColor,
} from '@/lib/browser-svggen-extra-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserGithubBadgeGenerator() {
  const [label, setLabel] = useState(DEFAULT_BADGE.label);
  const [message, setMessage] = useState(DEFAULT_BADGE.message);
  const [labelColor, setLabelColor] = useState(DEFAULT_BADGE.labelColor);
  const [color, setColor] = useState(DEFAULT_BADGE.color);
  const [logoText, setLogoText] = useState(DEFAULT_BADGE.logoText);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const options = useMemo(
    () => ({
      label,
      message,
      labelColor: sanitizeColor(labelColor, '#555'),
      color: sanitizeColor(color, '#4c1'),
      logoText,
    }),
    [label, message, labelColor, color, logoText],
  );

  const svg = useMemo(() => buildBadgeSvg(options), [options]);
  const markdown = useMemo(() => buildBadgeMarkdown(options), [options]);
  const html = useMemo(() => buildBadgeHtml(options), [options]);

  async function handleCopy(value: string, name: string) {
    try {
      await copyText(value);
      setCopyMessage(`${name} copied to clipboard.`);
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Badge</span>
        <h2 className="tool-live-title">GitHub badge generator</h2>
        <p className="tool-live-subtitle">
          Create shields.io-style status badges for your README with a custom label, message, and
          colors. Copy the SVG, Markdown, or HTML snippet, generated locally in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Label</span>
              <input onChange={(e) => setLabel(e.target.value)} type="text" value={label} />
            </label>
            <label className="tool-live-field">
              <span>Message</span>
              <input onChange={(e) => setMessage(e.target.value)} type="text" value={message} />
            </label>
            <label className="tool-live-field">
              <span>Logo text (optional)</span>
              <input
                onChange={(e) => setLogoText(e.target.value)}
                placeholder="npm"
                type="text"
                value={logoText}
              />
            </label>
            <label className="tool-live-field">
              <span>Label color</span>
              <input
                onChange={(e) => setLabelColor(e.target.value)}
                type="color"
                value={sanitizeColor(labelColor, '#555')}
              />
            </label>
            <label className="tool-live-field">
              <span>Message color</span>
              <input
                onChange={(e) => setColor(e.target.value)}
                type="color"
                value={sanitizeColor(color, '#4c1')}
              />
            </label>
            <label className="tool-live-field">
              <span>Color preset</span>
              <select onChange={(e) => setColor(e.target.value)} value={color}>
                {BADGE_COLOR_PRESETS.map((option) => (
                  <option key={`${option.value}-${option.label}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => handleCopy(svg, 'SVG')}
              type="button"
            >
              Copy SVG
            </button>
            <button
              className="button button-secondary"
              onClick={() => handleCopy(markdown, 'Markdown')}
              type="button"
            >
              Copy Markdown
            </button>
            <button
              className="button button-secondary"
              onClick={() => handleCopy(html, 'HTML')}
              type="button"
            >
              Copy HTML
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <span
            style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}
          >
            Live preview
          </span>
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '80px',
            }}
          >
            <span dangerouslySetInnerHTML={{ __html: svg }} />
          </div>
          <label className="tool-live-field tool-live-field-full" style={{ marginTop: '16px' }}>
            <span>SVG markup</span>
            <textarea readOnly rows={4} value={svg} />
          </label>
          <label className="tool-live-field tool-live-field-full">
            <span>Markdown</span>
            <textarea readOnly rows={2} value={markdown} />
          </label>
          <label className="tool-live-field tool-live-field-full">
            <span>HTML</span>
            <textarea readOnly rows={2} value={html} />
          </label>
          <p className="tool-live-panel-note">
            Generated locally in your browser. Nothing is uploaded.
          </p>
        </section>
      </div>
    </div>
  );
}
